/**
 * Base de datos local Dexie/IndexedDB.
 * Adaptador temporal mientras se configura el backend real (SharePoint o Azure SQL).
 *
 * Uso: VITE_BACKEND=dexie en .env.local
 */

import Dexie, { type Table } from "dexie";
import type { OutboxEntry } from "../../../offline/outboxTypes";
import type {
  Afe,
  So,
  Tarea,
  Rd,
  Evento,
  Acta,
  ContratoMarco,
  Tarifa,
  Pr,
  Po,
  Usuario,
  AuditEntry,
  Budget,
  CosteCatalogo,
  ConfiguracionNotificacion,
  ListaDistribucion,
  NotificacionEnviada,
  ConfiguracionIA,
  Riesgo,
  Stakeholder,
  CatalogoRiesgos,
  RiskBurndownSnapshot,
  ListadoPendientes,
  PendienteComm,
  SistemaComm,
  SubsistemaComm,
  MejoraPmis,
  Mr,
  GastoSap,
  RegistroRecursos,
} from "@app-types/entities";

/**
 * Capa 1 (jerarquia VD: Budget -> AFE -> Coste): cambiamos el nombre de la BD
 * para garantizar arranque limpio del modelo de datos. La BD vieja "PmisLocalDb"
 * queda huerfana en IndexedDB hasta que el navegador la recicle.
 */
export class PmisDb extends Dexie {
  budgets!: Table<Budget, string>;
  costesCatalogo!: Table<CosteCatalogo, string>;
  afes!: Table<Afe, string>;
  contratosMarco!: Table<ContratoMarco, string>;
  tarifas!: Table<Tarifa, string>;
  prs!: Table<Pr, string>;
  pos!: Table<Po, string>;
  sos!: Table<So, string>;
  tareas!: Table<Tarea, string>;
  rds!: Table<Rd, string>;
  eventos!: Table<Evento, string>;
  actas!: Table<Acta, string>;
  usuarios!: Table<Usuario, string>;
  auditLog!: Table<AuditEntry, string>;
  configuracionesNotificacion!: Table<ConfiguracionNotificacion, string>;
  listasDistribucion!: Table<ListaDistribucion, string>;
  notificacionesEnviadas!: Table<NotificacionEnviada, string>;
  configuracionesIA!: Table<ConfiguracionIA, string>;
  riesgos!: Table<Riesgo, string>;
  stakeholders!: Table<Stakeholder, string>;
  catalogosRiesgos!: Table<CatalogoRiesgos, string>;
  riskBurndownSnapshots!: Table<RiskBurndownSnapshot, string>;
  // Comisionamiento — punch list (v7).
  listadosPendientes!: Table<ListadoPendientes, string>;
  pendientesComm!: Table<PendienteComm, string>;
  sistemasComm!: Table<SistemaComm, string>;
  subsistemasComm!: Table<SubsistemaComm, string>;
  // Mejoras PMIS — feedback cross-gerencia del propio sistema (v10).
  mejoras!: Table<MejoraPmis, string>;
  // MR (Material Requisition) — pedido contra CM de materiales (v11).
  mrs!: Table<Mr, string>;
  // Gastos SAP (AC real) — registro de gastos causados importados de SAP (v12).
  gastosSap!: Table<GastoSap, string>;
  registrosRecursos!: Table<RegistroRecursos, string>;
  /** Bandeja de salida para la sincronización offline (Pieza C). */
  outbox!: Table<OutboxEntry, string>;

  constructor() {
    // DB renombrada para que la build v2 NO comparta IndexedDB con la v1.
    // El sufijo `-app2` mantiene la base "PmisLocalDbV2" original (que es
    // un identificador de schema, no del build) y agrega disambiguación
    // por instancia de aplicación.
    super("PmisLocalDbV2-app2");

    this.version(1).stores({
      // Llave primaria + indices
      budgets: "id, tenantId, codigo, estado, fechaInicio",
      costesCatalogo: "id, tenantId, codigo, tipo, activo",
      afes: "id, tenantId, codigo, budgetId, tipo, estado, createdAt",
      contratosMarco: "id, tenantId, codigo, contratistaId, estado",
      tarifas: "id, codigo, [contratistaNombre+codigo]",
      prs: "id, tenantId, afeId, estado",
      pos: "id, tenantId, proveedorId, estado",
      sos: "id, tenantId, codigo, contratistaId, interventorId, estado",
      tareas: "id, soId, codigo",
      rds: "id, tenantId, soId, fecha, estado",
      eventos: "id, tenantId, soId, rdId, tipo, fecha",
      actas: "id, tenantId, soId, codigo, estado",
      usuarios: "id, email, rol",
      auditLog: "id, tenantId, timestamp, usuario, entidad",
    });

    // Versión 2: tabla de configuración de notificaciones por correo.
    // Migración aditiva — no toca datos existentes.
    this.version(2).stores({
      configuracionesNotificacion: "id, tenantId, handoverId, modulo, habilitado",
    });

    // Versión 3: añade los campos `tipo` ("accion" | "tiempo"), `asunto`
    // y `cuerpoTpl` a `ConfiguracionNotificacion`. El índice nuevo es
    // `tipo` para poder filtrar reglas de tiempo desde el panel de
    // alertas. Migración: rellena `tipo` en las filas existentes
    // tomándolo del catálogo de handovers (lookup por `handoverId`).
    this.version(3)
      .stores({
        configuracionesNotificacion: "id, tenantId, handoverId, modulo, tipo, habilitado",
      })
      .upgrade(async (tx) => {
        const { HANDOVERS_CATALOGO } = await import(
          "@features/notificaciones/handoversCatalog"
        );
        const tipoByHandoverId = new Map(
          HANDOVERS_CATALOGO.map((h) => [h.id, h.tipo] as const),
        );
        await tx
          .table<ConfiguracionNotificacion>("configuracionesNotificacion")
          .toCollection()
          .modify((row) => {
            if (!row.tipo) {
              // Default a "accion" si el handoverId desapareció del catálogo
              // (caso muy improbable; preservamos la fila por integridad).
              row.tipo = tipoByHandoverId.get(row.handoverId) ?? "accion";
            }
          });
      });

    // Versión 4: tablas para el subsistema de envío de correos.
    //  - `listasDistribucion`: rol/destinatario lógico → emails reales.
    //  - `notificacionesEnviadas`: bitácora append-only de cada envío.
    // Migración puramente aditiva — no toca datos existentes.
    this.version(4).stores({
      listasDistribucion: "id, tenantId, destinatario",
      notificacionesEnviadas: "id, tenantId, handoverId, actor, fechaIntento, estado",
    });

    // Versión 5: configuración de IA (singleton por tenant). Una sola
    // fila por tenant con el proveedor activo + API keys de los demás
    // proveedores configurados. Migración puramente aditiva.
    this.version(5).stores({
      configuracionesIA: "id, tenantId",
    });

    // Versión 6: Gestión de Riesgos + Stakeholders.
    //  - `riesgos`: registro principal del Risk Register.
    //  - `stakeholders`: matriz de interesados.
    //  - `catalogosRiesgos`: singleton con RBS, escalas P/I, umbrales.
    //  - `riskBurndownSnapshots`: snapshots mensuales para el chart histórico.
    // Migración puramente aditiva.
    this.version(6).stores({
      riesgos: "id, tenantId, codigo, estado, nivel, sprintAsignado, responsableEmail",
      stakeholders: "id, tenantId, codigo, cuadrante, estado, responsableEmail",
      catalogosRiesgos: "id, tenantId",
      riskBurndownSnapshots: "id, tenantId, mes",
    });

    // Versión 7: Comisionamiento (listados de pendientes / punch list).
    //  - `listadosPendientes`: agrupador por AFE, define modalidad
    //    (POR_AFE simple vs POR_PROTOCOLO_COMM extenso).
    //  - `pendientesComm`: pendientes individuales con foto antes/después.
    //  - `sistemasComm` / `subsistemasComm`: matriz de sistemas para la
    //    modalidad de Protocolo Comm.
    // Migración puramente aditiva.
    this.version(7).stores({
      listadosPendientes: "id, tenantId, afeId, tipo, codigo",
      pendientesComm:
        "id, tenantId, listadoId, estado, categoria, disciplina, soId, sistemaId, subsistemaId, responsableEmail",
      sistemasComm: "id, tenantId, listadoId, codigo",
      subsistemasComm: "id, tenantId, sistemaId, codigo",
    });

    // Versión 8: añade índice `tenantId` a la tabla `usuarios` para que el
    // LocalAuthProvider y el seed puedan filtrar por tenant sin scan completo.
    // La falta de este índice causaba `SchemaError` silencioso en el provider
    // → la pantalla de login se quedaba con dropdown vacío. Migración
    // aditiva: sólo añade índice, no modifica datos.
    this.version(8).stores({
      usuarios: "id, tenantId, email, rol",
    });

    // Versión 9: multi-gerencia F3 — añade índice `gerenciaId` a las tablas
    // afectadas por el ADR "De País a Gerencia" (Fases 3 + 4). También
    // añade `compartido` a riesgos y eventos para el sharing flag de Fase 4.
    //
    // Migración aditiva: no toca datos viejos. Los registros sin
    // `gerenciaId` quedan con `undefined` y los queries los tratan como
    // "sin gerencia asignada" → invisibles excepto al admin con
    // `verTodasLasGerencias`. El seed posterior los repone con la
    // gerencia natural (CO).
    this.version(9).stores({
      costesCatalogo: "id, tenantId, gerenciaId, codigo, activo",
      contratosMarco: "id, tenantId, gerenciaId, codigo, contratistaId, estado",
      stakeholders: "id, tenantId, gerenciaId, codigo, cuadrante, estado, responsableEmail",
      riesgos:
        "id, tenantId, gerenciaId, compartido, codigo, estado, nivel, sprintAsignado, responsableEmail",
      catalogosRiesgos: "id, tenantId, gerenciaId",
      configuracionesNotificacion:
        "id, tenantId, gerenciaId, handoverId, modulo, tipo, habilitado",
      listasDistribucion: "id, tenantId, gerenciaId, destinatario",
      configuracionesIA: "id, tenantId, gerenciaId",
      eventos: "id, tenantId, soId, rdId, afeId, tipo, fecha, compartido",
    });

    // Versión 10: módulo "Mejoras PMIS" — repositorio cross-gerencia de
    // hallazgos, observaciones y recomendaciones que cualquier usuario
    // puede registrar mientras prueba el PMIS. Sin gerenciaId — es un
    // meta-módulo del sistema. El ADMIN_PMIS responde y cambia estado.
    this.version(10).stores({
      mejoras: "id, tenantId, fechaReporte, reportadoPor, modulo, estado",
    });

    // Versión 11: módulo "Materiales por CM" — Material Requisitions (MR)
    // contra contratos marco de tipo MATERIALES. PL2 emite → ALMACEN
    // gestiona la entrega → ABAS registra el pago. Hereda gerencia vía
    // AFE→Budget (igual que Po), por eso no tiene índice de gerencia.
    this.version(11).stores({
      mrs: "id, tenantId, codigo, afeId, contratoMarcoId, estado, fechaEmision",
    });

    // Versión 12: módulo "Gastos SAP" — registro de AC real importado del
    // reporte "BD Mes". Se indexa a coste/AFE/budget. Hereda gerencia vía
    // AFE→Budget (no tiene índice de gerencia). `docSap` es la llave natural
    // de deduplicación (Nº documento SAP + posición).
    this.version(12).stores({
      gastosSap:
        "id, tenantId, afeCodigo, afeId, budgetCodigo, costeCodigo, docSap, contabilizado",
    });
    this.version(13).stores({
      // Bandeja de salida (outbox) para la sincronización offline.
      outbox: "id, tenantId, entity, estado, createdAt",
    });
    // Versión 14: módulo "Registro de recursos". La llave natural es
    // (contratistaId, fecha) — un solo registro por contratista y día.
    this.version(14).stores({
      registrosRecursos: "id, tenantId, contratistaId, fecha",
    });
  }
}

export const localDb = new PmisDb();
